import { useCallback, useState } from 'react';
import { usePathname } from 'expo-router';
import { trpc } from '../lib/trpc';
import { trackConnectionLinkEvent } from '../lib/product-events';

const DEFAULT_TELLER_SCRIPT_URL = 'https://cdn.teller.io/connect/connect.js';

interface TellerHandler {
  open: () => void;
}

interface TellerEnrollmentPayload {
  accessToken: string;
  user: { id: string };
  enrollment: {
    id: string;
    institution?: {
      name?: string | null;
    } | null;
  };
  signatures?: string[];
}

interface TellerSetupConfig {
  applicationId: string;
  environment: 'sandbox' | 'development' | 'production';
  products: string[];
  selectAccount?: 'disabled' | 'single' | 'multiple';
  enrollmentId?: string;
  nonce: string;
  onSuccess: (payload: TellerEnrollmentPayload) => void;
  onExit?: () => void;
}

declare global {
  interface Window {
    TellerConnect?: {
      setup: (config: TellerSetupConfig) => TellerHandler;
    };
  }
}

function loadTellerScript(scriptUrl: string): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.TellerConnect) return Promise.resolve();

  const existing = document.querySelector(`script[src="${scriptUrl}"]`) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Teller script failed to load')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Teller Connect'));
    document.head.appendChild(script);
  });
}

export interface TellerConnectionOptions {
  onSuccess: (connectionId: string) => void;
  onError?: (error: string) => void;
  connectionId?: string;
}

export function useTellerConnection({
  onSuccess,
  onError,
  connectionId,
}: TellerConnectionOptions) {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTellerSessionMutation = trpc.connections.createTellerSession.useMutation();
  const authorizeTellerMutation = trpc.connections.authorizeTeller.useMutation();

  const open = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    trackConnectionLinkEvent({
      route: pathname,
      screen: 'connections',
      provider: 'teller',
      status: 'started',
      metadata: {
        isUpdateMode: connectionId != null,
      },
    });

    try {
      const session = await createTellerSessionMutation.mutateAsync(
        connectionId ? { connectionId } : {},
      );

      await loadTellerScript(session.scriptUrl ?? DEFAULT_TELLER_SCRIPT_URL);

      if (!window.TellerConnect) {
        throw new Error('Teller Connect failed to initialize');
      }

      const handler = window.TellerConnect.setup({
        applicationId: session.applicationId,
        environment: session.environment,
        products: session.products,
        selectAccount: 'multiple',
        nonce: session.nonce,
        ...(session.enrollmentId ? { enrollmentId: session.enrollmentId } : {}),
        onSuccess: async (payload) => {
          setIsLoading(true);
          try {
            const result = await authorizeTellerMutation.mutateAsync({
              accessToken: payload.accessToken,
              userId: payload.user.id,
              enrollmentId: payload.enrollment.id,
              institutionName: payload.enrollment.institution?.name ?? undefined,
              nonce: session.nonce,
              signatures: payload.signatures ?? [],
            });
            trackConnectionLinkEvent({
              route: pathname,
              screen: 'connections',
              provider: 'teller',
              status: 'succeeded',
              metadata: {
                isUpdateMode: connectionId != null,
                institutionName: payload.enrollment.institution?.name ?? null,
              },
            });
            onSuccess(result.connectionId);
          } catch (err) {
            const msg =
              err instanceof Error ? err.message : 'Failed to connect bank account';
            trackConnectionLinkEvent({
              route: pathname,
              screen: 'connections',
              provider: 'teller',
              status: 'failed',
              metadata: {
                isUpdateMode: connectionId != null,
                stage: 'authorize',
                errorMessage: msg,
              },
            });
            setError(msg);
            onError?.(msg);
          } finally {
            setIsLoading(false);
          }
        },
        onExit: () => {
          setIsLoading(false);
          trackConnectionLinkEvent({
            route: pathname,
            screen: 'connections',
            provider: 'teller',
            status: 'exited',
            metadata: {
              isUpdateMode: connectionId != null,
            },
          });
        },
      });

      handler.open();
      trackConnectionLinkEvent({
        route: pathname,
        screen: 'connections',
        provider: 'teller',
        status: 'opened',
        metadata: {
          isUpdateMode: connectionId != null,
        },
      });
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to open Teller Connect';
      trackConnectionLinkEvent({
        route: pathname,
        screen: 'connections',
        provider: 'teller',
        status: 'failed',
        metadata: {
          isUpdateMode: connectionId != null,
          stage: 'open',
          errorMessage: msg,
        },
      });
      setError(msg);
      onError?.(msg);
      setIsLoading(false);
    }
  }, [
    authorizeTellerMutation,
    connectionId,
    createTellerSessionMutation,
    onError,
    onSuccess,
    pathname,
  ]);

  return {
    open,
    isLoading:
      isLoading
      || createTellerSessionMutation.isPending
      || authorizeTellerMutation.isPending,
    error,
  };
}
